import React, { useState } from "react"

const pageNarrative = `
The direction behind these concepts is to create a mark that feels modern, distinctive, and clinically sharp without becoming generic or overly soft. The core idea began with the cervical spine, using 7 horizontal lines to represent the 7 cervical vertebrae in an abstract way. Those lines were intentionally misaligned to suggest adjustment, movement, and the chiropractic relationship between structure and alignment.

As the concepts evolved, the goal became to refine that symbol into something more architectural, premium, and memorable. Equal-width bars introduced a cleaner sense of consistency and precision. Different colour variations were then explored to test how the same core mark could shift in tone, from high-end and prestigious, to crisp and clinical, to grounded and natural.

Throughout the process, the strongest ideas remained the same: keep the symbol abstract rather than literal, make the count meaningful, and create a mark that can stand on its own without relying on common chiropractic clichés. The final exploration also opens the door to a broader spine-based interpretation, connecting not only the 7 cervical vertebrae, but also the wider idea of the 33 vertebrae and the role of alignment across the whole body.
`

const logos = [
  {
    src: "/assets/blog/logos/chiro_refined_forest.png",
    title: "Chiro Refined Forest",
    text: "A refined chiropractic mark presented in forest green. This version feels grounded and trustworthy, with a calm and stable visual character that could appeal to a clinic wanting a natural but still professional identity.",
  },
  {
    src: "/assets/blog/logos/final_no_33_charcoal.png",
    title: "Final No 33 Charcoal",
    text: "A later evolution that shifts the concept toward a broader spine interpretation. This direction is intended to feel modern, eye-catching, and memorable, with potential to connect chiropractic, the number 33, and vertebral alignment in a less literal way.",
  },
  {
    src: "/assets/blog/logos/final_no_33_navy.png",
    title: "Final No 33 Navy",
    text: "A navy variation of the broader spine-based concept. The darker palette gives it a more clinical and established feel, while the abstract construction keeps it contemporary and suitable for a distinctive clinic identity.",
  },
  {
    src: "/assets/blog/logos/alt_concept_3_balance.png",
    title: "Alt Concept 3 Balance",
    text: "This concept explores the idea of balance more explicitly. The form is still abstract, but the visual emphasis shifts toward stability, symmetry, and centred control, aligning well with chiropractic outcomes and body function.",
  },
  {
    src: "/assets/blog/logos/7 lines absolute with Peach tone.png",
    title: "7 Lines Absolute with Peach Tone",
    text: "A refined variation using 7 misaligned horizontal lines to symbolise the 7 cervical vertebrae. This version keeps the mark abstract and elegant, with the peach tone softening the clinical feel and giving it a warmer, more premium presentation.",
  },
  {
    src: "/assets/blog/logos/7_lines_absolute_gold.png",
    title: "7 Lines Absolute Gold",
    text: "A stronger high-end expression of the original 7-line concept. The gold treatment gives the mark a more established and premium feel, while the misalignment keeps the symbol connected to adjustment, precision, and chiropractic alignment.",
  },
  {
  src: "/assets/blog/logos/7_lines_equal_alternating_gold.png",
  title: "7 Lines Alternating Gold",
  text: "This concept uses 7 tapered bars, subtly narrowing toward one end to introduce direction and movement. The alternating gold treatment adds visual rhythm while the taper creates a more dynamic, less rigid interpretation of alignment.",
},
{
  src: "/assets/blog/logos/7_lines_equal_alternating_Peach.png",
  title: "7 Lines Alternating Peach",
  text: "A softer version of the tapered bar concept. The narrowing lines create flow and progression, while the peach palette keeps the overall feel warm, calm, and approachable without losing the geometric intent.",
},
  {
    src: "/assets/blog/logos/7_lines_forest.png",
    title: "7 Lines Forest",
    text: "A grounded version of the 7-line symbol using a forest green palette. This gives the concept a stable, natural, and professional tone while still retaining the abstract representation of the cervical spine.",
  },
  {
    src: "/assets/blog/logos/alt_concept_1_precision_peach.png",
    title: "Alt Concept 1 Precision Peach",
    text: "A variation built around the idea of precision and restraint. The symbol feels more controlled and deliberate, with the peach tone softening the overall expression and making it feel more boutique and refined.",
  },
  {
    src: "/assets/blog/logos/alt_concept_1_precision.png",
    title: "Alt Concept 1 Precision",
    text: "A more neutral and sharper version of the precision concept. This direction leans into clean geometry and disciplined spacing, aiming for a mark that feels modern, intelligent, and distinctly premium.",
  },
  {
  src: "/assets/blog/logos/final_7_lines_chiro.png",
  title: "Final 7 Lines Chiro",
  text: "A circular badge concept built around 7 stacked horizontal bars, using a high-end seal format to make the symbol feel more established and complete. The central stack remains abstract and spine-inspired, while the curved typography helps frame it as a distinctive clinic identity.",
},
  {
    src: "/assets/blog/logos/simple_streamline.png",
    title: "Simple Streamline",
    text: "A simplified concept that focuses on clarity and memorability. This direction reduces visual complexity and explores whether a cleaner, more streamlined symbol could create stronger recall while still linking back to spine and alignment themes.",
  },
]

export default function Logos() {
  const [activeLogo, setActiveLogo] = useState(null)

  const closeModal = () => setActiveLogo(null)

  return (
    <>
      <div
        style={{
          padding: "80px 24px 80px",
          background: "#f7f3ef",
          minHeight: "100vh",
        }}
      >
        <div
          style={{
            maxWidth: "1280px",
            margin: "0 auto",
          }}
        >
          <h1
            style={{
              margin: "0 0 10px",
              fontSize: "clamp(2rem, 4vw, 3rem)",
              lineHeight: 1.1,
            }}
          >
            Thirty Three Chiropractic Logo Concepts
          </h1>

          <p
            style={{
              margin: "0 0 28px",
              fontSize: "0.95rem",
              fontStyle: "italic",
              color: "#5a514c",
            }}
          >
            *Click images to view larger picture
          </p>

          <div
            style={{
              maxWidth: "980px",
              marginBottom: "40px",
              padding: "24px",
              background: "#ffffff",
              border: "1px solid #e7ddd4",
              borderRadius: "18px",
            }}
          >
            <h2
              style={{
                margin: "0 0 12px",
                fontSize: "1.25rem",
              }}
            >
              Narrative
            </h2>

            <p
              style={{
                margin: 0,
                lineHeight: 1.7,
                color: "#3f3a37",
                whiteSpace: "pre-line",
              }}
            >
              {pageNarrative}
            </p>

            <p
              style={{
                margin: "18px 0 0",
                lineHeight: 1.6,
                color: "#5a514c",
                fontStyle: "italic",
              }}
            >
              All colours can be changed.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: "24px",
            }}
          >
            {logos.map((logo, index) => (
              <article
                key={index}
                style={{
                  background: "#ffffff",
                  border: "1px solid #e7ddd4",
                  borderRadius: "18px",
                  overflow: "hidden",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
                }}
              >
                <button
                  type="button"
                  onClick={() => setActiveLogo(logo)}
                  style={{
                    display: "block",
                    width: "100%",
                    border: 0,
                    background: "#fbf8f5",
                    padding: "18px",
                    cursor: "pointer",
                  }}
                  aria-label={`Open ${logo.title}`}
                >
                  <img
                    src={logo.src}
                    alt={logo.title}
                    style={{
                      width: "100%",
                      height: "220px",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </button>

                <div style={{ padding: "18px" }}>
                  <h3
                    style={{
                      margin: "0 0 10px",
                      fontSize: "1rem",
                      lineHeight: 1.3,
                    }}
                  >
                    {logo.title}
                  </h3>

                  <div
                    style={{
                      minHeight: "140px",
                      padding: "14px",
                      borderRadius: "12px",
                      background: "#f7f3ef",
                      color: "#4c4642",
                      lineHeight: 1.6,
                      fontSize: "0.95rem",
                    }}
                  >
                    {logo.text}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>

      {activeLogo && (
        <div
          onClick={closeModal}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Escape") closeModal()
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.72)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            zIndex: 2000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              width: "min(720px, 100%)",
              maxHeight: "88vh",
              overflowY: "auto",
              background: "#ffffff",
              borderRadius: "20px",
              padding: "24px",
              boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
            }}
          >
            <button
              type="button"
              onClick={closeModal}
              aria-label="Close"
              style={{
                position: "absolute",
                top: "14px",
                right: "14px",
                width: "40px",
                height: "40px",
                borderRadius: "999px",
                border: "1px solid #d8cec5",
                background: "#ffffff",
                color: "#2f2a27",
                fontSize: "1.4rem",
                lineHeight: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>

            <h2
              style={{
                margin: "0 0 18px",
                paddingRight: "52px",
                fontSize: "1.35rem",
                lineHeight: 1.2,
              }}
            >
              {activeLogo.title}
            </h2>

            <div
              style={{
                background: "#fbf8f5",
                borderRadius: "16px",
                padding: "18px",
                marginBottom: "18px",
              }}
            >
              <img
                src={activeLogo.src}
                alt={activeLogo.title}
                style={{
                  width: "100%",
                  maxHeight: "60vh",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </div>

            <div
              style={{
                padding: "18px",
                borderRadius: "14px",
                background: "#f7f3ef",
                color: "#4c4642",
                lineHeight: 1.7,
              }}
            >
              {activeLogo.text}
            </div>
          </div>
        </div>
      )}
    </>
  )
}